import React, { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import CareScreenerService, { InsuranceEstimate } from "../../services/CareScreenerService";
import ConsultLoading from "./CareScreenerLoading";
import { reportError } from "../../utils/errorUtils";
import { StyleSheet, css } from "aphrodite";
import { screenerStyles } from "./careScreenerStyles";
import InsuranceEligibleCoverage from "./insurance/InsuranceEligibleCoverage";
import InsuranceUnknownCoverage from "./insurance/InsuranceUnknownCoverage";
import { CareScreenerContext } from "@context/CareScreenerContext";
import { EVERNORTH_TYPEFORM_ID } from "../../utils/constants";
import EvernorthIneligible from "./EvernorthIneligible";

type Props = {
  careScreenerSubmission: {
    id: number;
    typeformId: string;
    typeformResponseId: string;
  };
  onContinue: () => void;
  progressComponent?: ReactNode;
  onPasswordSubmitted: (value: string | undefined) => Promise<void>;
  consultPath: string;
  skipCheck?: boolean;
};

const loadingTextVariants = [
  { text: "Saving your responses...", subText: "This should only take a few seconds." },
  { text: "Checking your payment options...", subText: "If you provided insurance, we're checking your coverage too." }
];

export default function InsuranceEligibility({
  careScreenerSubmission,
  onContinue,
  progressComponent,
  onPasswordSubmitted,
  consultPath,
  skipCheck
}: Props) {
  const { setInsuranceEligibilityStatus: setEligibilityStatus, insuranceEligibilityStatus: eligibilityStatus } =
    useContext(CareScreenerContext)!;

  const [view, setView] = useState("loading");
  const [insuranceEstimate, setInsuranceEstimate] = useState<InsuranceEstimate>();

  const checkInsuranceEligibility = useCallback(async () => {
    try {
      await CareScreenerService.checkCareScreenerInsuranceEligibility({
        careScreenerSubmissionId: careScreenerSubmission.id,
        typeformId: careScreenerSubmission.typeformId,
        typeformResponseId: careScreenerSubmission.typeformResponseId
      }).then(r => {
        setInsuranceEstimate(r[0]?.insuranceEstimate);
        setEligibilityStatus(r[0]?.eligibilityStatus || "UNKNOWN");
      });
    } catch (error) {
      setEligibilityStatus("UNKNOWN");
      reportError(
        `checkInsuranceEligibility -- typeformReponseId=${careScreenerSubmission?.typeformResponseId} -- ${error}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!skipCheck) {
      checkInsuranceEligibility();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLoadingTextRotation = useCallback(() => {
    if (eligibilityStatus) {
      if (eligibilityStatus === "NOT_APPLICABLE") {
        onContinue();
      } else {
        setView("eligibility");
      }
    }
  }, [eligibilityStatus, onContinue]);

  return (
    <>
      {view === "loading" && (
        <div
          className={css(
            screenerStyles.whiteContainerRoundedEdges,
            screenerStyles.columnCentered,
            screenerStyles.padding
          )}
        >
          {progressComponent}
          <ConsultLoading textVariants={loadingTextVariants} onTextRotation={onLoadingTextRotation} />
          <p className={css(styles.footerText)}>
            "My child is super proud of himself and I am still shocked at how easy a transition it was for him."
            <br />
            -Manatee parent
          </p>
        </div>
      )}
      {view === "eligibility" && (
        <>
          {eligibilityStatus === "ELIGIBLE" ? (
            <InsuranceEligibleCoverage
              insuranceEstimate={insuranceEstimate}
              progressComponent={progressComponent}
              onPasswordSubmitted={onPasswordSubmitted}
              consultPath={consultPath}
            />
          ) : careScreenerSubmission.typeformId === EVERNORTH_TYPEFORM_ID ? (
            <EvernorthIneligible />
          ) : (
            <InsuranceUnknownCoverage
              progressComponent={progressComponent}
              onPasswordSubmitted={onPasswordSubmitted}
              consultPath={consultPath}
            />
          )}
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  footerText: {
    textAlign: "center",
    color: "#1A2579"
  }
});
